import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGridPro, DataGridProProps, GridRowId, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { ExpandIcon } from './ExpandIcon';
import { useIsGrouping } from './hooks';
import palette from 'theme/palette';
import { Checkbox } from 'ui';
import { forwardRef } from 'react';

const DataGrid = styled(DataGridPro, {
  shouldForwardProp: prop => prop !== 'hideActions' && prop !== 'isRowClickable'
})<{
  isRowClickable?: boolean;
  hideActions?: boolean;
}>(({ theme, hideActions, isRowClickable }) => {
  return {
    overflowY: 'scroll',
    ...(hideActions
      ? {
          '& .MuiDataGrid-row': {
            '& .MuiDataGrid-actionsCell': {
              visibility: 'visible'
            }
          },
          '& .MuiDataGrid-row:not(.Mui-selected)': {
            '& .MuiDataGrid-actionsCell': {
              visibility: 'hidden'
            }
          }
        }
      : {}),
    '& .MuiDataGrid-row:hover:not(.MuiDataGrid-row--detailPanelExpanded)': {
      backgroundColor: palette.secondary.light,
      '.MuiDataGrid-actionsCell': {
        visibility: 'visible'
      },
      ...(isRowClickable ? { cursor: 'pointer' } : {})
    },
    '& .MuiDataGrid-row--detailPanelExpanded:hover': {
      backgroundColor: theme.palette.secondary.main,
      '.MuiDataGrid-actionsCell': {
        visibility: 'visible'
      },
      ...(isRowClickable ? { cursor: 'pointer' } : {})
    },
    '& .MuiDataGrid-row:active': {
      backgroundColor: theme.palette.secondary.main,
      '.MuiDataGrid-actionsCell': {
        visibility: 'visible'
      },
      ...(isRowClickable ? { cursor: 'pointer' } : {})
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: 6,
      backgroundColor: theme.palette.secondary.main,
      '&.Mui-selected': {
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.secondary.main,
        '&:hover': {
          backgroundColor: theme.palette.secondary.light
        }
      }
    },
    '& .MuiDataGrid-row-open': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    },
    '& .MuiDataGrid-row--detailPanelExpanded': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      backgroundColor: theme.palette.secondary.main
    },
    '& .MuiDataGrid-row-group': {
      backgroundColor: 'hsla(0, 0%, 100%, 0.04)',
      border: 0,
      borderRadius: 0
    },
    '& .MuiDataGrid-row-group:hover': {
      backgroundColor: theme.palette.secondary.light
    },
    '& .MuiDataGrid-row-child-last': {
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6
    },
    '& .MuiDataGrid-cell, &.MuiDataGrid-root': {
      border: 'none'
    },
    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none'
    },
    '& .MuiDataGrid-virtualScroller': {
      paddingInlineEnd: '5px',
      '::-webkit-scrollbar': {
        width: 4
      },
      '::-webkit-scrollbar-track': {
        background: 'transparent'
      },
      '::-webkit-scrollbar-thumb': {
        background: theme.palette.transparent.white[24],
        borderRadius: 10
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: theme.palette.transparent.white[48]
      },
      position: 'relative'
    },
    '& .MuiDataGrid-virtualScroller:hover': {
      overflow: 'auto'
    },
    '& .MuiDataGrid-virtualScrollerRenderZone': {
      width: '100%',
      position: 'relative'
    },
    '& .MuiDataGrid-columnSeparator': {
      color: 'transparent',
      '&:hover': {
        color: ''
      }
    },
    '& .MuiDataGrid-treeDataGroupingCell > span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    '& .MuiDataGrid-columnHeader--moving': {
      backgroundColor: 'transparent'
    },
    '& .MuiDataGrid-pinnedColumns': {
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      boxShadow: 'none'
    },
    '& .MuiDataGrid-pinnedColumns > .Mui-hovered': {
      backgroundColor: 'transparent',
      border: 'none'
    },
    '& .MuiDataGrid-pinnedColumns > .MuiDataGrid-row-group': {
      backgroundColor: 'transparent',
      border: 'none'
    },
    '& .MuiDataGrid-pinnedColumns > .MuiDataGrid-row': {
      backgroundColor: 'transparent',
      border: 'none'
    },
    '& .MuiDataGrid-pinnedColumnHeaders': {
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      boxShadow: 'none'
    },
    '& .MuiDataGrid-detailPanel': {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
      width: '99.7%'
    },
    '& .MuiDataGrid-detailPanels': {
      position: 'inherit'
    },
    '& .MuiTypography-root.MuiLink-root:hover': {
      color: palette.secondary.link,
      textDecoration: 'underline'
    }
  };
}) as typeof DataGridPro;

type withError = {
  error?: boolean;
  onErrorRefresh?: () => void;
};

type extraProps = {
  actionsNotHidden?: boolean;
  processedRowId?: GridRowId;
};

const ErrorPage = styled('div')({
  display: 'flex',
  alignItems: 'center',
  paddingTop: 100,
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  gap: 32
});

const ErrorMessages = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column'
});

const StyledDataGrid = <R extends GridValidRowModel = any>({
  error,
  onErrorRefresh,
  actionsNotHidden,
  groupingColDef,
  ...props
}: DataGridProProps<R> & withError & extraProps) => {
  const apiRefNew = useGridApiRef();
  const apiRef = props.apiRef ?? apiRefNew;

  const { getIsGroupSubRow } = useIsGrouping(apiRef);

  if (error) {
    return (
      <ErrorPage>
        <Icon name="search" width={34} height={34} />
        <ErrorMessages>
          <Typography variant="h5">Failed Loading Data</Typography>
          <Typography variant="body2">Reload or try again later</Typography>
        </ErrorMessages>
        {onErrorRefresh && <Button onClick={onErrorRefresh}>Refresh</Button>}
      </ErrorPage>
    );
  }

  return (
    <DataGrid
      // @ts-ignore: type of datagrid is not correct
      hideActions={!actionsNotHidden}
      isRowClickable={!props?.disableSelectionOnClick || Boolean(props?.getDetailPanelContent)}
      groupingColDef={
        groupingColDef ?? {
          headerName: '',
          valueFormatter: () => '',
          resizable: false,
          width: 0,
          sortable: false
        }
      }
      apiRef={apiRef}
      {...props}
      components={{
        BaseCheckbox: forwardRef((props, ref) => {
          const refPropName = Object.keys(props).find(key => /ref/i.test(key));

          const forwardedProps = {
            ...props,
            ...(refPropName ? { [refPropName]: ref } : { ref })
          };

          return <Checkbox {...forwardedProps} />;
        }),
        DetailPanelExpandIcon: () => <ExpandIcon />,
        DetailPanelCollapseIcon: () => <ExpandIcon expanded />,
        ...props.components
      }}
      componentsProps={props.componentsProps}
      getRowClassName={params => {
        let className = '';
        const rowNode = apiRef.current.getRowNode(params.id);
        if (rowNode?.childrenExpanded) {
          className += 'MuiDataGrid-row-open ';
        }
        if (getIsGroupSubRow(params.row)) {
          className += 'MuiDataGrid-row-group ';
        }
        if (rowNode?.parent && apiRef.current.getRowNode(rowNode.parent)?.children?.at(-1) === params.id) {
          className += 'MuiDataGrid-row-child-last ';
        }
        return className;
      }}
      getRowSpacing={params => {
        if (getIsGroupSubRow(params.model)) {
          return {
            top: 0,
            bottom: 0
          };
        }
        return {
          top: 6,
          bottom: params.isLastVisible ? 6 : 0
        };
      }}
    />
  );
};

export { DataGridPro as UnStyledDataGrid };

export default StyledDataGrid;
